import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Btn, Empty } from "@/components/common";
import { Modal, Field } from "@/components/Modal";
import { PRIO, TYPE_META, TYPES, REPLY_STATUS, STATUSES, IMPACT, TYPE_DEFAULT_PRIO, dueLabel } from "@/lib/ticketMeta";
import { Ticket, Plus, AlertTriangle, Sparkles } from "lucide-react";
import { timeAgo } from "@/lib/ui";
import { toast } from "sonner";
import { onLive } from "@/lib/live";

export { TicketDetail } from "./TicketDetail";

export default function Tickets() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);
  const [statusF, setStatusF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [onlyBreached, setOnlyBreached] = useState(false);
  const [form, setForm] = useState({ customer_id: "", ticket_type: "Incident", subject: "", description: "", priority: "P3" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSel, setBulkSel] = useState(new Set());
  const [applying, setApplying] = useState(false);

  const load = () => {
    const params = {};
    if (statusF) params.status = statusF;
    if (typeF) params.ticket_type = typeF;
    if (onlyBreached) params.breached = true;
    api.get("/tickets", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, typeF, onlyBreached]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    const off = onLive((e) => { if (e.type === "ticket") load(); });
    return () => off();
    /* eslint-disable-next-line */
  }, []);

  const setType = (t) => setForm((f) => ({ ...f, ticket_type: t, priority: TYPE_DEFAULT_PRIO[t] || f.priority }));
  const create = async () => {
    try { await api.post("/tickets", form); toast.success("Ticket created"); setShow(false); setForm({ customer_id: "", ticket_type: "Incident", subject: "", description: "", priority: "P3" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openDetail = (t) => nav("/tickets/" + t.id);
  const openBulk = async () => {
    setBulkOpen(true); setBulkLoading(true); setBulkRows([]); setBulkSel(new Set());
    try {
      const { data } = await api.post("/copilot/bulk-triage", { limit: 20 });
      setBulkRows(data.suggestions);
      setBulkSel(new Set(data.suggestions.filter((s) => s.changed).map((s) => s.ticket_id)));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBulkLoading(false); }
  };
  const toggleBulk = (id) => setBulkSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const applyBulk = async () => {
    const picks = bulkRows.filter((r) => bulkSel.has(r.ticket_id));
    if (picks.length === 0) return toast.error("Select at least one ticket");
    setApplying(true);
    try {
      await Promise.all(picks.map((r) => api.patch("/tickets/" + r.ticket_id, { priority: r.suggested_priority, ticket_type: r.suggested_type })));
      toast.success(`Applied triage to ${picks.length} ticket${picks.length > 1 ? "s" : ""}`);
      setBulkOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setApplying(false); }
  };

  if (!rows) return <div className="p-6"><Spinner /></div>;
  const breachedCount = rows.filter((t) => t.sla_response_breached || t.sla_resolution_breached).length;

  return (
    <div className="p-6 fade-up">
      <PageHeader title="Tickets" subtitle={`${rows.length} tickets · ${breachedCount} SLA-breached`} testid="tickets-page"
        actions={<>
          <select value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="ticket-type-filter"
            className="bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-sm text-slate-200 outline-none">
            <option value="">All types</option>{TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="ticket-status-filter"
            className="bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-sm text-slate-200 outline-none">
            <option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button data-testid="ticket-breached-toggle" onClick={() => setOnlyBreached((v) => !v)}
            className={"text-[13px] rounded-md px-3 py-1.5 border font-semibold " + (onlyBreached ? "bg-rose-950/60 border-rose-800 text-rose-300" : "bg-slate-800/70 border-slate-700 text-slate-300")}>
            <AlertTriangle size={13} className="inline mr-1" />SLA breached
          </button>
          {can("copilot:use") && <Btn data-testid="bulk-triage-btn" variant="ghost" onClick={openBulk}><Sparkles size={15} /> Bulk triage</Btn>}
          {can("tickets:create") && <Btn data-testid="new-ticket-btn" onClick={() => setShow(true)}><Plus size={15} /> New Ticket</Btn>}
        </>} />

      {rows.length === 0 ? <Empty icon={Ticket} title="No tickets" hint="Create a ticket or let alert rules auto-generate them." /> : (<>
        <div className="hidden md:block card-surface rounded-lg overflow-x-auto">
          <table className="w-full text-left text-[13px] min-w-[900px]" data-testid="tickets-table">
            <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-b border-slate-800">
              <th className="px-3 py-2">#</th><th className="px-3 py-2">Subject</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Assignee</th><th className="px-3 py-2">Priority</th><th className="px-3 py-2">SLA</th>
              <th className="px-3 py-2">Reply</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Updated</th></tr></thead>
            <tbody className="divide-y divide-slate-800/60">
              {rows.map((t) => {
                const tm = TYPE_META[t.ticket_type] || TYPE_META.Incident;
                const pm = PRIO[t.priority] || PRIO.P3;
                return (
                  <tr key={t.id} className="hover-row cursor-pointer" data-testid="ticket-row" onClick={() => openDetail(t)}>
                    <td className="px-3 py-2 font-mono text-cyan-400">#{t.number}{t.source && t.source !== "manual" && <span className="ml-1 text-[9px] text-amber-500 uppercase">auto</span>}</td>
                    <td className="px-3 py-2 text-slate-200">{t.subject}</td>
                    <td className="px-3 py-2"><span data-testid="ticket-type" className="inline-flex items-center gap-1 text-[12px] text-slate-300"><tm.icon size={13} className={tm.cls} />{t.ticket_type || "Incident"}</span></td>
                    <td className="px-3 py-2 text-slate-400">{t.customer_name}</td>
                    <td className="px-3 py-2 text-slate-400">{t.assigned_to ? t.assigned_to.split("@")[0] : <span className="text-slate-600">—</span>}</td>
                    <td className="px-3 py-2"><span data-testid="ticket-priority" className={"text-[10px] font-bold uppercase rounded border px-1.5 py-0.5 " + pm.cls} title={pm.impact}>{t.priority}</span></td>
                    <td className="px-3 py-2">
                      {(t.sla_response_breached || t.sla_resolution_breached)
                        ? <span data-testid="ticket-sla-breach" className="text-[10px] font-bold uppercase text-rose-300 bg-rose-950/60 border border-rose-800 rounded px-1.5 py-0.5">Breached</span>
                        : <span className="text-[10px] text-emerald-400/80">{dueLabel(t.sla_resolution_due, false) || "—"}</span>}
                    </td>
                    <td className="px-3 py-2">{t.reply_status ? <span data-testid="ticket-reply-status" className={"text-[10px] font-bold uppercase rounded border px-1.5 py-0.5 whitespace-nowrap " + (REPLY_STATUS[t.reply_status] || REPLY_STATUS.Read)}>{t.reply_status === "Awaiting Technician Response" ? "Awaiting Tech" : t.reply_status === "Awaiting Customer Response" ? "Awaiting Cust" : t.reply_status}</span> : <span className="text-slate-600">—</span>}</td>
                    <td className="px-3 py-2"><span className="text-[10px] font-mono uppercase text-slate-400 bg-slate-800 rounded px-1.5 py-0.5">{t.status}</span></td>
                    <td className="px-3 py-2 text-slate-500">{timeAgo(t.updated_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2" data-testid="tickets-cards">
          {rows.map((t) => { const pm = PRIO[t.priority] || PRIO.P3; return (
            <button key={t.id} data-testid="ticket-card" onClick={() => openDetail(t)} className="w-full text-left card-surface rounded-lg p-3">
              <div className="flex items-center gap-2"><span className="font-mono text-cyan-400 text-[13px]">#{t.number}</span><span className={"text-[10px] font-bold uppercase rounded border px-1.5 py-0.5 " + pm.cls}>{t.priority}</span><span className="ml-auto text-[10px] font-mono uppercase text-slate-400 bg-slate-800 rounded px-1.5 py-0.5">{t.status}</span></div>
              <div className="text-[13px] text-slate-100 mt-1 font-medium">{t.subject}</div>
              <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-2"><span>{t.customer_name}</span><span>· {t.ticket_type}</span><span>· {timeAgo(t.updated_at)}</span>{(t.sla_response_breached || t.sla_resolution_breached) && <span className="text-rose-400">· SLA breached</span>}</div>
            </button>
          ); })}
        </div>
      </>)}

      {show && (
        <Modal title="New Ticket" onClose={() => setShow(false)}>
          <Field label="Customer"><select data-testid="ticket-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="inp"><option value="">Select…</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
          <Field label="Ticket type">
            <div className="grid grid-cols-2 gap-2 mb-1" data-testid="ticket-type-picker">
              {TYPES.map((t) => { const M = TYPE_META[t]; return (
                <button key={t} type="button" data-testid={`type-opt-${t.toLowerCase()}`} onClick={() => setType(t)}
                  className={"flex items-start gap-2 text-left rounded-md border px-2.5 py-2 " + (form.ticket_type === t ? "border-cyan-700 bg-cyan-950/30" : "border-slate-700 bg-slate-900/60")}>
                  <M.icon size={15} className={M.cls + " mt-0.5 shrink-0"} />
                  <span><span className="block text-[12px] font-semibold text-slate-200">{t}</span><span className="block text-[10px] text-slate-500 leading-tight">{M.desc}</span></span>
                </button>
              ); })}
            </div>
          </Field>
          <Field label="Subject"><input data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="inp" /></Field>
          <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="inp" /></Field>
          <Field label="Priority — how many users are impacted?">
            <div className="space-y-1 mb-1" data-testid="impact-picker">
              {IMPACT.map((i) => (
                <button key={i.p} type="button" data-testid={`impact-${i.p}`} onClick={() => setForm({ ...form, priority: i.p })}
                  className={"w-full flex items-center gap-2 text-left rounded-md border px-2.5 py-1.5 text-[12px] " + (form.priority === i.p ? "border-cyan-700 bg-cyan-950/30 text-slate-100" : "border-slate-700 bg-slate-900/60 text-slate-300")}>
                  <span className={"text-[10px] font-bold uppercase rounded border px-1.5 py-0.5 " + PRIO[i.p].cls}>{i.p}</span>{i.label}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancel</Btn><Btn data-testid="ticket-save-btn" onClick={create} disabled={!form.customer_id || !form.subject}>Create</Btn></div>
        </Modal>
      )}
      {bulkOpen && (
        <Modal title="Bulk Triage — untriaged (New) tickets" onClose={() => setBulkOpen(false)} wide testid="bulk-triage-modal">
          {bulkLoading ? <div className="py-8 text-center"><Spinner /><p className="text-[12px] text-slate-500 mt-2">Copilot is reviewing New tickets…</p></div> : (
            bulkRows.length === 0 ? <Empty icon={Sparkles} title="Nothing to triage" hint="No New tickets found in your scope." /> : (<>
              <p className="text-[12px] text-slate-400 mb-3">Reviewed {bulkRows.length} New tickets. Suggested changes are pre-selected — untick any you want to skip.</p>
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto mb-4">
                {bulkRows.map((r) => (
                  <label key={r.ticket_id} data-testid="bulk-triage-row" className={"flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer " + (bulkSel.has(r.ticket_id) ? "border-cyan-700 bg-cyan-950/20" : "border-slate-700 bg-slate-900/60") + (r.changed ? "" : " opacity-70")}>
                    <input type="checkbox" data-testid="bulk-triage-check" checked={bulkSel.has(r.ticket_id)} onChange={() => toggleBulk(r.ticket_id)} className="accent-cyan-500 mt-1" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-[13px]"><span className="font-mono text-cyan-400">#{r.number}</span><span className="text-slate-200 truncate">{r.subject}</span></div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{r.customer_name}</div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px]">
                        <span className={"font-bold uppercase rounded border px-1.5 py-0.5 " + (PRIO[r.current_priority] || PRIO.P3).cls}>{r.current_priority}</span>
                        <span className="text-slate-500">{r.current_type}</span>
                        {r.changed ? <span className="text-cyan-400 mx-1">→</span> : <span className="text-slate-600 mx-1">=</span>}
                        <span className={"font-bold uppercase rounded border px-1.5 py-0.5 " + (PRIO[r.suggested_priority] || PRIO.P3).cls}>{r.suggested_priority}</span>
                        <span className="text-slate-300">{r.suggested_type}</span>
                      </div>
                      {r.reason && <div className="text-[11px] text-slate-500 italic mt-0.5">{r.reason}</div>}
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Btn><Btn data-testid="bulk-triage-apply" onClick={applyBulk} disabled={applying || bulkSel.size === 0}>{applying ? "Applying…" : `Apply to ${bulkSel.size} selected`}</Btn></div>
            </>)
          )}
        </Modal>
      )}
      <style>{`.inp{width:100%;background:#0b1220;border:1px solid #334155;border-radius:6px;padding:8px 12px;font-size:13px;color:#e2e8f0;outline:none;margin-bottom:12px}`}</style>
    </div>
  );
}
